import React, { FunctionComponent, useState, MouseEvent, useEffect } from 'react';
import styles from './HeaderTop.module.scss';
import { WPMenu, WPMenuItem } from 'types/menus';
import { Icon, LinkWrapper, Typography } from 'components/elements';
import cx from 'clsx';
import { ArrowRight as ArrowRightIcon, ArrowLeft as ArrowLeftIcon } from 'assets/icons';
import { isMobile } from 'utils';
import { useIntl } from 'react-intl';
import messages from 'messages/base-pl';
import { useLocation } from 'react-router-dom';
import { useCurrentWidth } from 'hooks/dom';
import { createMenuLink } from 'utils/links';
import LanguagePicker from "../LanguagePicker/LanguagePicker";

interface OwnProps {
  menu?: WPMenu | null;
  onItemClick?: () => void;
}

type HeaderTopProps = JSX.IntrinsicElements['div'] & OwnProps;

const highlightedSlugs = ['kontakt', 'contact'];

const HeaderTop: FunctionComponent<HeaderTopProps> = props => {
  const { formatMessage } = useIntl();
  const { menu, onItemClick = () => {}, ...rest } = props;
  const { items = [] } = menu || {};
  const [activeSumbenu, setActiveSubmenu] = useState<number | null>(null);
  const location = useLocation();
  const windowWidth = useCurrentWidth();

  useEffect(() => {
    setActiveSubmenu(null);
  }, [windowWidth]);

  const handleLinkClick = (hasSubmenu: boolean, id: number) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (isMobile() && hasSubmenu) {
      event.preventDefault();
      setActiveSubmenu(activeSumbenu ? null : id);
    } else {
      onItemClick();
      setActiveSubmenu(null);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container} {...rest}>
        {items.map((item: WPMenuItem) => {
          const hasSubitems = item.child_items && item.child_items.length > 0;

          const itemClassNames = cx({
            [styles.item]: true,
            [styles.hasSubmenu]: hasSubitems,
            [styles.activeItem]: activeSumbenu === item.ID,
            [styles.active]: location.pathname?.includes(`/${item.slug}`),
            [styles.hideItem]: activeSumbenu && activeSumbenu !== item.ID,
            [styles.highlighted]: highlightedSlugs.includes(item?.slug || ''),
          });

          const submenuClassNames = cx({
            [styles.submenu]: true,
            [styles.activeSumbenu]: activeSumbenu === item.ID && hasSubitems,
          });

          return (
            <div key={item.ID} className={itemClassNames}>
              {/* @ts-ignore */}
              <LinkWrapper className={styles.link} onClick={handleLinkClick(!!hasSubitems, item.ID)} href={createMenuLink(item)}>
                <Typography mobile="new_neue_16" tablet="new_neue_12" desktop="new_neue_12" as="span">
                  {item.title}
                </Typography>
                {!activeSumbenu && hasSubitems && <Icon size={20} icon={ArrowRightIcon} />}
                {activeSumbenu === item.ID && hasSubitems && <Icon size={20} icon={ArrowLeftIcon} />}
                {activeSumbenu === item.ID && (
                  <Typography mobile="new_neue_12" tablet="new_neue_12" desktop="new_neue_12" as="span">
                    {formatMessage(messages.header_menu)}
                  </Typography>
                )}
              </LinkWrapper>
              {hasSubitems && (
                <div key={item.ID} className={submenuClassNames}>
                  {item?.child_items?.map(subitem => {
                    const nestedLinkClassNames = cx({
                      [styles.item]: true,
                      [styles.active]: location.pathname?.includes(`/${item.slug}/${subitem.slug}`),
                      [styles.nested]: true,
                    });
                    return (
                      <div key={`header-top-item-subitem-${subitem.ID}`} className={nestedLinkClassNames}>
                        <LinkWrapper className={styles.link} onClick={handleLinkClick(false, subitem.ID)} href={createMenuLink(subitem, item.slug)}>
                          <Typography mobile="new_neue_16" tablet="new_neue_12" desktop="new_neue_12" as="span">
                            {subitem.title}
                          </Typography>
                        </LinkWrapper>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        <div className={styles.language}>
          <LanguagePicker />
        </div>
      </div>
    </div>
  );
};

export default HeaderTop;
